import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
} from 'react-bootstrap';
import Select from 'react-select';
import debounce from 'lodash.debounce';
import CustomButton from '../../Components/Common/CustomButton/CustomButton';
import { useGlobalContext } from '../../Context/index';
import {
    getActiveMinisBanners,
    getAllMinis,
    Mini,
    MinisBanner,
    updateMinisBanners,
    UpdateMinisBannerPayload,
} from './api';

type SelectOption = { value: string; label: string };

const selectMenuPortalProps = {
    menuPortalTarget: document.body,
    styles: { menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }) },
};

const parseMiniIdFromDeeplink = (openDeeplink: string): string | null => {
    try {
        const parsed = JSON.parse(openDeeplink);
        if (parsed?.screen === 'Minis' && parsed?.params?.id) {
            return parsed.params.id;
        }
    } catch {
        // ignore
    }
    return null;
};

const buildMinisDeeplink = (miniId: string) =>
    JSON.stringify({ screen: 'Minis', params: { id: miniId } });

const toDatetimeLocal = (iso: string) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
};

const fromDatetimeLocal = (value: string) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const defaultEndDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString();
};

const bannerToPayload = (banner: MinisBanner): UpdateMinisBannerPayload => ({
    id: banner.id,
    imageUrl: banner.imageUrl,
    rank: banner.rank,
    startdate: banner.startdate,
    enddate: banner.enddate,
    action: banner.action || 'openDeeplink',
    openWebviewUrl: banner.openWebviewUrl || '',
    openDeeplink: banner.openDeeplink,
    isactive: banner.isactive,
});

interface BannerFormState {
    id?: string;
    imageUrl: string;
    rank: string;
    startdate: string;
    enddate: string;
    mini: SelectOption | null;
}

const emptyBannerForm = (): BannerFormState => ({
    imageUrl: '',
    rank: '1',
    startdate: new Date().toISOString(),
    enddate: defaultEndDate(),
    mini: null,
});

const miniToOption = (mini: Mini): SelectOption => ({
    value: mini.id,
    label: mini.title,
});

const mergeMiniOptions = (
    selected: SelectOption | null,
    options: SelectOption[],
): SelectOption[] => {
    if (!selected) return options;
    if (options.some(o => o.value === selected.value)) return options;
    return [selected, ...options];
};

const MinisBanners: React.FC = () => {
    const { setShowSuccessToast, setShowErrorToast } = useGlobalContext();

    const [banners, setBanners] = useState<MinisBanner[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const [showMiniPicker, setShowMiniPicker] = useState(false);
    const [showBannerForm, setShowBannerForm] = useState(false);
    const [bannerForm, setBannerForm] = useState<BannerFormState>(emptyBannerForm);
    const [formError, setFormError] = useState('');
    const [bannerToDelete, setBannerToDelete] = useState<MinisBanner | null>(null);

    const [miniOptions, setMiniOptions] = useState<SelectOption[]>([]);
    const [miniById, setMiniById] = useState<Record<string, Mini>>({});
    const [miniPickerSelection, setMiniPickerSelection] = useState<SelectOption | null>(null);
    const [miniPickerLoading, setMiniPickerLoading] = useState(false);

    const [miniTitles, setMiniTitles] = useState<Record<string, string>>({});

    const fetchBanners = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const active = await getActiveMinisBanners();
            setBanners(
                active.map(b => ({
                    ...b,
                    startdate: b.startdate ?? '',
                    enddate: b.enddate ?? '',
                    isactive: 1,
                })),
            );
        } catch (err: unknown) {
            const message =
                err instanceof Error ? err.message : 'Failed to load tab banners';
            setError(message);
            setBanners([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBanners();
    }, [fetchBanners]);

    useEffect(() => {
        const ids = [
            ...new Set(
                banners
                    .map(b => parseMiniIdFromDeeplink(b.openDeeplink))
                    .filter((id): id is string => Boolean(id)),
            ),
        ];
        if (ids.length === 0) return;

        getAllMinis({ count: 50, includeInactive: true })
            .then(minis => {
                setMiniTitles(prev => {
                    const next = { ...prev };
                    let changed = false;
                    minis.forEach(m => {
                        if (ids.includes(m.id) && !next[m.id]) {
                            next[m.id] = m.title;
                            changed = true;
                        }
                    });
                    return changed ? next : prev;
                });
            })
            .catch(() => {
                // non-blocking
            });
    }, [banners]);

    const loadMinis = useCallback(async (search?: string) => {
        setMiniPickerLoading(true);
        try {
            const minis = await getAllMinis({
                count: 50,
                searchquery: search,
                includeInactive: false,
            });
            const options = minis.map(miniToOption);
            setMiniOptions(options);
            setMiniById(prev => {
                const next = { ...prev };
                minis.forEach(m => {
                    next[m.id] = m;
                });
                return next;
            });
            setMiniTitles(prev => {
                const next = { ...prev };
                minis.forEach(m => {
                    next[m.id] = m.title;
                });
                return next;
            });
        } catch {
            // keep existing options
        } finally {
            setMiniPickerLoading(false);
        }
    }, []);

    const debouncedLoadMinis = useMemo(
        () => debounce((value: string) => loadMinis(value), 300),
        [loadMinis],
    );

    useEffect(() => () => debouncedLoadMinis.cancel(), [debouncedLoadMinis]);

    const handleMiniInputChange = (value: string, actionMeta: { action: string }) => {
        if (actionMeta.action !== 'input-change') return;
        debouncedLoadMinis(value);
    };

    const syncBanners = async (payload: UpdateMinisBannerPayload[]) => {
        setSaving(true);
        setError('');
        try {
            const updated = await updateMinisBanners(payload);
            setBanners(updated);
            setShowSuccessToast(true);
        } catch (err: unknown) {
            const message =
                err instanceof Error ? err.message : 'Failed to update tab banners';
            setError(message);
            setShowErrorToast(true);
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const openAddFlow = () => {
        setMiniPickerSelection(null);
        setShowMiniPicker(true);
        loadMinis();
    };

    const handleMiniPicked = () => {
        if (!miniPickerSelection) return;
        const mini = miniById[miniPickerSelection.value];
        setShowMiniPicker(false);
        setBannerForm({
            imageUrl: mini?.thumbnailurl ?? '',
            rank: String(banners.length + 1),
            startdate: new Date().toISOString(),
            enddate: defaultEndDate(),
            mini: miniPickerSelection,
        });
        setFormError('');
        setShowBannerForm(true);
    };

    const openEditBanner = (banner: MinisBanner) => {
        const miniId = parseMiniIdFromDeeplink(banner.openDeeplink);
        const miniLabel = miniId ? miniTitles[miniId] ?? miniId : '—';
        setBannerForm({
            id: banner.id,
            imageUrl: banner.imageUrl,
            rank: String(banner.rank),
            startdate: banner.startdate || new Date().toISOString(),
            enddate: banner.enddate || defaultEndDate(),
            mini: miniId ? { value: miniId, label: miniLabel } : null,
        });
        setFormError('');
        setShowBannerForm(true);
    };

    const handleSaveBanner = async () => {
        const imageUrl = bannerForm.imageUrl.trim();
        if (!imageUrl) {
            setFormError('Image URL is required.');
            return;
        }
        if (!bannerForm.mini) {
            setFormError('Linked mini is required.');
            return;
        }
        if (!bannerForm.startdate || !bannerForm.enddate) {
            setFormError('Start and end dates are required.');
            return;
        }

        const newBanner: UpdateMinisBannerPayload = {
            ...(bannerForm.id ? { id: bannerForm.id } : {}),
            imageUrl,
            rank: Number(bannerForm.rank) || 1,
            startdate: bannerForm.startdate,
            enddate: bannerForm.enddate,
            action: 'openDeeplink',
            openWebviewUrl: '',
            openDeeplink: buildMinisDeeplink(bannerForm.mini.value),
        };

        const others = banners
            .filter(b => b.id !== bannerForm.id)
            .map(bannerToPayload);

        setFormError('');
        try {
            await syncBanners([...others, newBanner]);
            setShowBannerForm(false);
        } catch {
            // error handled in syncBanners
        }
    };

    const handleDeleteBanner = async () => {
        if (!bannerToDelete) return;
        const remaining = banners
            .filter(b => b.id !== bannerToDelete.id)
            .map(bannerToPayload);
        try {
            await syncBanners(remaining);
            setBannerToDelete(null);
        } catch {
            setBannerToDelete(null);
        }
    };

    const getMiniLabel = (banner: MinisBanner) => {
        const miniId = parseMiniIdFromDeeplink(banner.openDeeplink);
        if (!miniId) return '—';
        return miniTitles[miniId] ?? miniId;
    };

    return (
        <Card className="mb-4">
            <Card.Header
                className="d-flex align-items-center justify-content-between"
                style={{ height: 60, minHeight: 60, padding: '0 16px' }}
            >
                <div style={{ lineHeight: 1.2 }}>
                    <strong style={{ fontSize: 14 }}>Tab Banners</strong>
                    <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                        Banners shown on the Minis tab (active and within schedule)
                    </div>
                </div>
                <CustomButton onClick={openAddFlow} disabled={saving}>
                    + Banner
                </CustomButton>
            </Card.Header>
            <Card.Body>
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" />
                    </div>
                ) : banners.length === 0 ? (
                    <div className="text-center text-muted py-3">No active tab banners</div>
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            overflowX: 'auto',
                            paddingBottom: 8,
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        {[...banners]
                            .sort((a, b) => a.rank - b.rank)
                            .map(banner => (
                                <div
                                    key={banner.id}
                                    style={{
                                        flex: '0 0 auto',
                                        width: 100,
                                        border: '1px solid #dee2e6',
                                        borderRadius: 6,
                                        overflow: 'hidden',
                                        background: '#fff',
                                    }}
                                >
                                    <div
                                        style={{
                                            position: 'relative',
                                            aspectRatio: '9 / 16',
                                            background: '#f1f3f5',
                                        }}
                                    >
                                        {banner.imageUrl ? (
                                            <img
                                                src={banner.imageUrl}
                                                alt=""
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                    display: 'block',
                                                }}
                                            />
                                        ) : null}
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: 4,
                                                left: 4,
                                                background: 'rgba(0,0,0,0.65)',
                                                color: '#fff',
                                                fontSize: 10,
                                                fontWeight: 600,
                                                padding: '1px 5px',
                                                borderRadius: 3,
                                            }}
                                        >
                                            #{banner.rank}
                                        </span>
                                    </div>
                                    <div style={{ padding: '5px 6px' }}>
                                        <div
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 500,
                                                marginBottom: 4,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                            title={getMiniLabel(banner)}
                                        >
                                            {getMiniLabel(banner)}
                                        </div>
                                        <div className="d-flex flex-column gap-1">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="py-0"
                                                style={{ fontSize: 11 }}
                                                onClick={() => openEditBanner(banner)}
                                                disabled={saving}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="py-0"
                                                style={{ fontSize: 11 }}
                                                onClick={() => setBannerToDelete(banner)}
                                                disabled={saving}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                )}
            </Card.Body>

            <Modal
                show={showMiniPicker}
                onHide={() => !miniPickerLoading && !saving && setShowMiniPicker(false)}
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Select Mini</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && <Alert variant="danger">{formError}</Alert>}
                    <Form.Group>
                        <Form.Label>Mini *</Form.Label>
                        <Select
                            isSearchable
                            placeholder="Search minis..."
                            options={mergeMiniOptions(miniPickerSelection, miniOptions)}
                            value={miniPickerSelection}
                            onChange={val => setMiniPickerSelection(val as SelectOption | null)}
                            onInputChange={handleMiniInputChange}
                            filterOption={() => true}
                            isLoading={miniPickerLoading}
                            {...selectMenuPortalProps}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowMiniPicker(false)}
                        disabled={miniPickerLoading || saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleMiniPicked}
                        disabled={!miniPickerSelection || miniPickerLoading || saving}
                    >
                        {miniPickerLoading ? (
                            <Spinner size="sm" animation="border" />
                        ) : (
                            'Continue'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showBannerForm}
                onHide={() => !saving && setShowBannerForm(false)}
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {bannerForm.id ? 'Edit Tab Banner' : 'Add Tab Banner'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && <Alert variant="danger">{formError}</Alert>}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Linked Mini</Form.Label>
                            <Form.Control
                                value={bannerForm.mini?.label ?? '—'}
                                disabled
                                readOnly
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Image URL *</Form.Label>
                            <Form.Control
                                value={bannerForm.imageUrl}
                                onChange={e =>
                                    setBannerForm(prev => ({
                                        ...prev,
                                        imageUrl: e.target.value,
                                    }))
                                }
                            />
                            {bannerForm.imageUrl && (
                                <img
                                    src={bannerForm.imageUrl}
                                    alt=""
                                    style={{
                                        marginTop: 8,
                                        width: 54,
                                        aspectRatio: '9 / 16',
                                        objectFit: 'contain',
                                        background: '#f1f3f5',
                                        borderRadius: 4,
                                    }}
                                />
                            )}
                        </Form.Group>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Rank</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={1}
                                        value={bannerForm.rank}
                                        onChange={e =>
                                            setBannerForm(prev => ({
                                                ...prev,
                                                rank: e.target.value,
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Start *</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={toDatetimeLocal(bannerForm.startdate)}
                                        onChange={e =>
                                            setBannerForm(prev => ({
                                                ...prev,
                                                startdate: fromDatetimeLocal(e.target.value),
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>End *</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={toDatetimeLocal(bannerForm.enddate)}
                                        onChange={e =>
                                            setBannerForm(prev => ({
                                                ...prev,
                                                enddate: fromDatetimeLocal(e.target.value),
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowBannerForm(false)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveBanner} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={!!bannerToDelete}
                onHide={() => !saving && setBannerToDelete(null)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Remove Tab Banner</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Remove this banner from the Minis tab? It will be deactivated.
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setBannerToDelete(null)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteBanner} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : 'Remove'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
};

export default MinisBanners;
